// ============================================
// PulseMD Type Definitions
// ============================================

// ---- User Types ----
export type UserRole = 'learner' | 'creator' | 'mentor' | 'admin' | 'org_admin';
export type SubscriptionTier = 'free' | 'premium' | 'pro';
export type CreatorTier = 'starter' | 'regular' | 'expert' | 'master';
export type TrainingLevel = 'medical_student' | 'resident' | 'fellow' | 'attending' | 'np_pa' | 'program_evaluator' | 'other';
export type Motivation = 'board_exam' | 'staying_sharp' | 'interviews' | 'exploring' | 'program_evaluation';
export type ReminderTime = 'morning' | 'lunch' | 'evening';
export type PushSoftAskStatus = 'not_asked' | 'deferred' | 'accepted' | 'declined' | 'exhausted';

export interface User {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  role: UserRole;
  subscription_tier: SubscriptionTier;
  created_at: string;
  updated_at: string;

  // Profile / Onboarding
  training_level?: TrainingLevel;
  specialty?: string;
  pgy_year?: number;
  onboarding_completed?: boolean;
  motivation?: Motivation;
  exam_date?: string; // 'YYYY-MM-DD'
  reminder_time?: ReminderTime | null; // null = user chose "No reminders"
  push_soft_ask_status?: PushSoftAskStatus;

  // Gamification
  xp: number;
  level: number;
  streak_days: number;
  longest_streak: number;
  streak_last_date?: string;
  questions_answered: number;
  questions_correct: number;
  questions_answered_today: number;
  last_activity_date?: string;

  // Settings
  daily_goal: number;
  notifications_enabled: boolean;
  public_profile: boolean;
  // Lifecycle email opt-ins (migration 41); kinds: daily_question,
  // streak_saver, weekly_progress, winback
  email_prefs?: Record<string, boolean | string>;

  // Interview Prep — opt-in for the end-of-session bonus block
  include_interview_prep?: boolean;

  // Streak Freeze
  streak_freezes_available: number;
  streak_freezes_used_this_month: number;
  is_streak_frozen: boolean;
  last_streak_freeze_date?: string;

  // Organization (for B2B)
  organization_id?: string;

  // Creator fields
  creator_tier?: CreatorTier;
  is_reviewer?: boolean;
  stripe_connect_id?: string;

  // Trial (Build A) — set when user signs up via a campaign slug
  trial_started_at?: string;
  trial_ends_at?: string;
  trial_campaign?: string;
  signup_campaign?: string;
  timezone?: string; // IANA zone; the server's "today" for this learner (migration 81) // the ?campaign= the account signed up through (migration 80)
  trial_converted?: boolean;
  trial_emails_sent?: Record<string, string>;

  // Lifetime Pro (shared between Build A grants and Build B referral approvals)
  lifetime_pro?: boolean;

  // Referral (Build B) — physician's NPI from outreach `?ref=` link
  referrer_npi?: string;

  // Acquisition attribution — captured from the landing URL at first profile
  // creation (email + Google OAuth). Powers paid-ad measurement end to end.
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string; // Google Ads click id
  fbclid?: string; // Meta (Facebook/Instagram) click id
}

export interface UserStats {
  user_id: string;
  total_xp: number;
  current_level: number;
  current_streak: number;
  longest_streak: number;
  questions_answered: number;
  questions_correct: number;
  accuracy_percentage: number;
  daily_goal_completed_today: boolean;
  questions_today: number;
  weekly_xp: number;
  category_stats: CategoryStat[];
}

export interface CategoryStat {
  category: QuestionCategory;
  questions_answered: number;
  questions_correct: number;
  accuracy: number;
  last_practiced?: string;
}

// ---- Gamification Types ----
export interface Level {
  level: number;
  name: string;
  xp_required: number;
  icon: string;
}

export const LEVELS: Level[] = [
  { level: 1, name: 'Seedling', xp_required: 0, icon: '🌱' },
  { level: 2, name: 'Sprout', xp_required: 100, icon: '🌿' },
  { level: 3, name: 'Sapling', xp_required: 300, icon: '🪴' },
  { level: 4, name: 'Budding', xp_required: 600, icon: '🌷' },
  { level: 5, name: 'Blooming', xp_required: 1000, icon: '🌸' },
  { level: 6, name: 'Flourishing', xp_required: 1500, icon: '🌺' },
  { level: 7, name: 'Thriving', xp_required: 2200, icon: '🌻' },
  { level: 8, name: 'Radiant', xp_required: 3000, icon: '💐' },
  { level: 9, name: 'Luminous', xp_required: 4000, icon: '✨' },
  { level: 10, name: 'Legendary', xp_required: 5500, icon: '🏆' },
];

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'streak' | 'accuracy' | 'volume' | 'social' | 'creator' | 'special';
  trigger_type: string;
  trigger_value: number;
  xp_reward: number;
}

export const ACHIEVEMENTS: Achievement[] = [
  { id: 'first_steps', name: 'First Steps', description: 'Answer your first question', icon: '🌱', category: 'volume', trigger_type: 'questions_answered', trigger_value: 1, xp_reward: 10 },
  { id: 'week_warrior', name: 'Week Warrior', description: '7 day streak', icon: '🔥', category: 'streak', trigger_type: 'streak_days', trigger_value: 7, xp_reward: 50 },
  { id: 'monthly_master', name: 'Monthly Master', description: '30 day streak', icon: '💪', category: 'streak', trigger_type: 'streak_days', trigger_value: 30, xp_reward: 200 },
  { id: 'centurion', name: 'Centurion', description: '100 day streak', icon: '🏆', category: 'streak', trigger_type: 'streak_days', trigger_value: 100, xp_reward: 500 },
  { id: 'sharp_mind', name: 'Sharp Mind', description: '90% accuracy on 50+ questions', icon: '🎯', category: 'accuracy', trigger_type: 'accuracy_50', trigger_value: 90, xp_reward: 100 },
  { id: 'century_club', name: 'Century Club', description: '100 questions answered', icon: '💯', category: 'volume', trigger_type: 'questions_answered', trigger_value: 100, xp_reward: 100 },
  { id: 'dedicated_learner', name: 'Dedicated Learner', description: '500 questions answered', icon: '📚', category: 'volume', trigger_type: 'questions_answered', trigger_value: 500, xp_reward: 250 },
  { id: 'board_ready', name: 'Board Ready', description: 'Reach Attending level', icon: '👨‍⚕️', category: 'special', trigger_type: 'level', trigger_value: 10, xp_reward: 500 },
  { id: 'ambassador', name: 'Ambassador', description: '5 successful referrals', icon: '🤝', category: 'social', trigger_type: 'referrals', trigger_value: 5, xp_reward: 200 },
  { id: 'giving_back', name: 'Giving Back', description: 'Complete first mentor session', icon: '🎓', category: 'social', trigger_type: 'mentor_sessions', trigger_value: 1, xp_reward: 100 },
  { id: 'content_creator', name: 'Content Creator', description: 'First question approved', icon: '✏️', category: 'creator', trigger_type: 'questions_created', trigger_value: 1, xp_reward: 100 },
  { id: 'top_performer', name: 'Top Performer', description: 'Reach weekly leaderboard top 10', icon: '🥇', category: 'special', trigger_type: 'leaderboard_top_10', trigger_value: 1, xp_reward: 150 },
];

export interface UserAchievement {
  id: string;
  user_id: string;
  achievement_id: string;
  earned_at: string;
}

// ---- XP Constants ----
export const XP_REWARDS = {
  CORRECT_ANSWER: 10,
  STREAK_3: 5,
  STREAK_5: 10,
  DAILY_GOAL: 25,
  PERFECT_SESSION: 50,
  STREAK_DAY: 5,
  HELPFUL_COMMENT: 5,
  REFERRAL: 100,
} as const;

// ---- Question Types ----
export type QuestionCategory =
  | 'cardiology'
  | 'endocrine'
  | 'respiratory'
  | 'gi'
  | 'preventive'
  | 'musculoskeletal'
  | 'pediatrics'
  | 'womens_health'
  | 'mental_health'
  | 'dermatology'
  | 'geriatrics'
  | 'substance_use'
  | 'renal_gu'
  | 'hematology'
  | 'infectious_disease'
  | 'foundations';

export const QUESTION_CATEGORIES: { value: QuestionCategory; label: string }[] = [
  { value: 'cardiology', label: 'Cardiology' },
  { value: 'endocrine', label: 'Endocrine/Diabetes' },
  { value: 'respiratory', label: 'Respiratory' },
  { value: 'gi', label: 'GI' },
  { value: 'preventive', label: 'Preventive Care' },
  { value: 'musculoskeletal', label: 'Musculoskeletal' },
  { value: 'pediatrics', label: 'Pediatrics' },
  { value: 'womens_health', label: "Women's Health" },
  { value: 'mental_health', label: 'Mental Health' },
  { value: 'dermatology', label: 'Dermatology' },
  { value: 'geriatrics', label: 'Geriatrics' },
  { value: 'substance_use', label: 'Substance Use' },
  { value: 'renal_gu', label: 'Renal/GU' },
  { value: 'hematology', label: 'Hematology' },
  { value: 'infectious_disease', label: 'Infectious Disease' },
  { value: 'foundations', label: 'Foundations of Care' },
];

export type QuestionDifficulty = 'easy' | 'medium' | 'hard';
export type QuestionStatus = 'draft' | 'submitted' | 'in_review' | 'approved' | 'rejected' | 'published';

export interface Question {
  id: string;
  stem: string; // Clinical vignette
  options: QuestionOption[];
  correct_option_index: number;
  explanation: string;
  wrong_explanations: string[]; // Explanation for each wrong answer
  // Client-only: set when practice shuffles options. option_order[displayedIndex] is the
  // stored index. submit_answer() grades against the stored correct_option_index.
  option_order?: number[];
  // Client-only: set by select_practice_questions (migration 78) when this is a scheduled
  // spaced-repetition review, so the card can say so instead of looking like a repeat.
  review?: { missed_last_time: boolean };
  category: QuestionCategory;
  difficulty: QuestionDifficulty;
  references: string[];
  tags: string[];
  status: QuestionStatus;

  // Creator info
  creator_id: string;
  creator_name?: string;

  // Review info
  reviewer_id?: string;
  review_notes?: string;
  reviewed_at?: string;

  // Stats
  times_shown: number;
  times_correct: number;

  created_at: string;
  updated_at: string;
  published_at?: string;

  // Interview Prep — undefined/'clinical' for the existing question population.
  question_type?: 'clinical' | 'interview';
  interview_format?: 'job' | 'fellowship' | null;
  interview_track?: string | null;
}

export interface QuestionOption {
  text: string;
  is_correct: boolean;
}

// ---- Spaced Repetition Types ----
export interface UserQuestionProgress {
  id: string;
  user_id: string;
  question_id: string;

  // SM-2 Algorithm fields
  ease_factor: number; // Default 2.5
  interval: number; // Days until next review
  repetitions: number; // Number of successful reviews
  next_review_date: string;

  // History
  times_seen: number;
  times_correct: number;
  last_seen_at: string;
  last_result: 'correct' | 'incorrect';

  // Bookmarking
  is_bookmarked: boolean;

  // Interview Prep — null for clinical Qs, 0|1|2 for interview Qs
  // (0=strong, 1=acceptable, 2=weak per the fixed storage convention).
  last_answer_index?: number | null;
}

// ---- Interview Prep Types ----

export type InterviewGradation = 'strong' | 'acceptable' | 'weak';

export type InterviewFormat = 'job' | 'fellowship';

/**
 * Storage convention for interview question options:
 *   options[0] = strong
 *   options[1] = acceptable
 *   options[2] = weak
 * Display order is shuffled per-render (see src/lib/interview/option-shuffle.ts).
 */
export const INTERVIEW_STORAGE_INDEX: Record<InterviewGradation, number> = {
  strong: 0,
  acceptable: 1,
  weak: 2,
};

export const INTERVIEW_TRACKS: { value: string; label: string }[] = [
  { value: 'behavioral',                     label: 'Behavioral' },
  { value: 'clinical-judgment',              label: 'Clinical Judgment' },
  { value: 'practice-management',            label: 'Practice Management' },
  { value: 'why-fm',                         label: 'Why FM' },
  { value: 'why-this-practice',              label: 'Why This Practice' },
  { value: 'scope-negotiation',              label: 'Scope Negotiation' },
  { value: 'salary',                         label: 'Salary & Benefits' },
  { value: 'fellowship-why-this',            label: 'Fellowship: Why This Field' },
  { value: 'fellowship-why-this-program',    label: 'Fellowship: Why This Program' },
];

// ---- Session Types ----
export interface PracticeSession {
  id: string;
  user_id: string;
  started_at: string;
  ended_at?: string;

  questions_attempted: number;
  questions_correct: number;
  xp_earned: number;
  streak_bonus: number;

  category_filter?: QuestionCategory;
  mode: 'practice' | 'review' | 'weak_areas' | 'category' | 'voice';
}

export interface SessionAnswer {
  id: string;
  session_id: string;
  question_id: string;
  selected_option: number;
  is_correct: boolean;
  time_spent_ms: number;
  answered_at: string;
}

// ---- Creator Types ----
export const CREATOR_TIERS = {
  starter: { name: 'Starter', per_question: 25, min_questions: 0, icon: '🌱' },
  regular: { name: 'Regular', per_question: 40, min_questions: 25, icon: '🌿' },
  expert: { name: 'Expert', per_question: 60, min_questions: 100, icon: '🌳' },
  master: { name: 'Master', per_question: 75, min_questions: 500, icon: '🌲' },
} as const;

export interface CreatorProfile {
  user_id: string;
  tier: CreatorTier;
  is_reviewer: boolean;

  // Stats
  questions_submitted: number;
  questions_approved: number;
  questions_rejected: number;

  // Earnings
  total_earnings: number;
  pending_earnings: number;
  last_payout_date?: string;

  // Revenue share (master tier)
  revenue_share_earnings: number;

  // Stripe
  stripe_connect_id?: string;
  stripe_connect_status: 'pending' | 'active' | 'disabled';

  created_at: string;
}

export interface CreatorPayout {
  id: string;
  creator_id: string;
  amount: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  stripe_transfer_id?: string;
  created_at: string;
  completed_at?: string;
}

// ---- Mentorship Types ----
export type MentorshipType = 'volunteer' | 'paid';
export type SessionStatus = 'scheduled' | 'completed' | 'cancelled' | 'no_show';

export interface MentorProfile {
  user_id: string;
  is_active: boolean;
  mentorship_type: MentorshipType;

  // Profile
  bio: string;
  specialties: string[];
  years_experience: number;
  credentials: string[];

  // Availability
  availability: MentorAvailability[];

  // Pricing (for paid)
  hourly_rate?: number;

  // Stats
  sessions_completed: number;
  average_rating: number;
  total_ratings: number;

  created_at: string;
}

export interface MentorAvailability {
  day_of_week: number; // 0-6 (Sunday-Saturday)
  start_time: string; // HH:MM format
  end_time: string;
}

export interface MentorSession {
  id: string;
  mentor_id: string;
  mentee_id: string;

  type: MentorshipType;
  status: SessionStatus;

  scheduled_at: string;
  duration_minutes: number;

  // For paid sessions
  price?: number;
  platform_fee?: number;
  stripe_payment_intent_id?: string;

  // Meeting
  meeting_url?: string;
  notes?: string;

  // Review
  rating?: number;
  review?: string;

  created_at: string;
}

// ---- Community Types ----
export interface StudyGroup {
  id: string;
  name: string;
  description: string;
  is_public: boolean;

  creator_id: string;
  member_count: number;
  max_members: number;

  // Challenge
  current_challenge_id?: string;

  created_at: string;
}

export interface StudyGroupMember {
  id: string;
  group_id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'member';
  joined_at: string;
}

export interface ForumPost {
  id: string;
  question_id?: string; // Optional link to a question
  author_id: string;
  author_name: string;
  author_avatar?: string;

  title: string;
  content: string;
  category?: string; // Forum category (general, study-tips, etc.)

  upvotes: number;
  reply_count: number;

  is_pinned: boolean;
  is_locked: boolean;

  created_at: string;
  updated_at: string;
}

export interface ForumReply {
  id: string;
  post_id: string;
  author_id: string;
  author_name: string;
  author_avatar?: string;

  content: string;
  upvotes: number;

  is_accepted: boolean;

  created_at: string;
  updated_at: string;
}

// ---- Question Comments Types ----
export interface QuestionComment {
  id: string;
  question_id: string;
  author_id: string;
  author_name: string;
  author_avatar?: string;

  content: string;
  upvotes: number;

  // For threaded discussions
  parent_id?: string;
  reply_count: number;

  is_helpful: boolean; // Marked as helpful by users
  is_pinned: boolean; // Pinned by moderators

  created_at: string;
  updated_at: string;
}

// ---- Success Stories (Pass Celebration) ----
export interface SuccessStory {
  id: string;
  user_id: string;
  author_name: string;
  author_avatar?: string;
  author_level: number;

  title: string;
  content: string;
  exam_name: string;
  exam_date?: string;

  passed: boolean;
  score?: string;

  is_public: boolean;
  upvotes: number;
  comment_count: number;

  photo_url?: string;

  is_featured: boolean;
  is_approved: boolean;

  created_at: string;
}

// ---- Leaderboard Types ----
export type LeaderboardPeriod = 'daily' | 'weekly' | 'monthly' | 'all_time';

export interface LeaderboardEntry {
  rank: number;
  user_id: string;
  user_name: string;
  user_avatar?: string;
  user_level: number;
  xp: number;
  questions_answered: number;
  accuracy: number;
}

// ---- B2B / Organization Types ----
export type OrgSubscriptionTier = 'basic' | 'pro' | 'enterprise';

export interface Organization {
  id: string;
  name: string;
  type: 'residency_program' | 'hospital' | 'medical_school' | 'other';

  subscription_tier: OrgSubscriptionTier;
  subscription_status: 'active' | 'past_due' | 'cancelled';

  // Limits
  max_seats: number;
  used_seats: number;

  // SSO
  sso_enabled: boolean;
  sso_provider?: string;
  sso_domain?: string;

  // Billing
  stripe_customer_id?: string;
  stripe_subscription_id?: string;

  // Settings
  privacy_settings: OrgPrivacySettings;

  created_at: string;
}

export interface OrgPrivacySettings {
  show_individual_progress: boolean;
  show_individual_scores: boolean;
  allow_leaderboard_opt_in: boolean;
}

export interface OrgMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: 'admin' | 'program_director' | 'resident';

  // Privacy
  show_on_leaderboard: boolean;
  share_progress: boolean;

  joined_at: string;
}

export interface OrgChallenge {
  id: string;
  organization_id: string;
  name: string;
  description: string;

  start_date: string;
  end_date: string;

  goal_type: 'questions' | 'xp' | 'accuracy' | 'streak';
  goal_value: number;

  prize_description?: string;

  is_active: boolean;
  created_at: string;
}

export interface OrgAnalytics {
  organization_id: string;
  period: 'week' | 'month' | 'quarter' | 'year';

  // Engagement
  active_users: number;
  total_questions_answered: number;
  average_questions_per_user: number;
  average_daily_active: number;

  // Performance
  average_accuracy: number;
  category_performance: { category: QuestionCategory; accuracy: number }[];

  // Progress
  users_completed_daily_goal: number;
  average_streak: number;

  // Comparison (anonymized)
  percentile_vs_similar_programs?: number;
}

// ---- B2B Referral Types (Build B) ----
export interface B2BReferral {
  id: string;
  org_id: string;
  org_name?: string;
  referrer_npi: string;
  referrer_user_id?: string;
  referrer_name?: string;
  referrer_email?: string;
  referred_user_id?: string;
  status: 'pending_approval' | 'approved' | 'denied';
  admin_note?: string;
  flagged_at: string;
  resolved_at?: string;
  resolved_by?: string;
  created_at: string;
}

// ---- Referral Types ----
export interface Referral {
  id: string;
  referrer_id: string;
  referred_email: string;
  referred_user_id?: string;

  status: 'pending' | 'signed_up' | 'converted' | 'expired';

  referral_code: string;
  xp_awarded: boolean;

  created_at: string;
  converted_at?: string;
}

// ---- Subscription & Payment Types ----
export interface Subscription {
  id: string;
  user_id: string;
  tier: SubscriptionTier;

  status: 'active' | 'past_due' | 'cancelled' | 'trialing';

  stripe_subscription_id?: string;
  stripe_customer_id?: string;

  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;

  created_at: string;
}

export const SUBSCRIPTION_PRICES = {
  free: { monthly: 0, yearly: 0 },
  premium: { monthly: 19, yearly: 190 },
  pro: { monthly: 29, yearly: 290 },
} as const;

export const B2B_PRICES = {
  basic: { per_resident_yearly: 50 },
  pro: { per_resident_yearly: 80 },
  enterprise: { custom: true },
} as const;

// ---- Feature Flags ----
export interface FeatureFlags {
  // Core
  'core.PRACTICE_MODE': boolean;
  'core.STUDY_PLAN': boolean;
  'core.SPACED_REPETITION': boolean;
  'core.WEAK_AREA_FOCUS': boolean;

  // Gamification
  'gamification.XP_SYSTEM': boolean;
  'gamification.LEVELS': boolean;
  'gamification.STREAKS': boolean;
  'gamification.ACHIEVEMENTS': boolean;
  'gamification.LEADERBOARDS': boolean;
  'gamification.WEEKLY_LEAGUES': boolean;

  // Community
  'community.FORUMS': boolean;
  'community.STUDY_GROUPS': boolean;
  'community.QUESTION_COMMENTS': boolean;

  // Growth
  'growth.REFERRAL_PROGRAM': boolean;
  'growth.PROGRAM_CHALLENGES': boolean;
  'growth.SHAREABLE_ACHIEVEMENTS': boolean;
  'growth.PUBLIC_PROFILES': boolean;
  'growth.CAMPAIGN_TRIALS': boolean;
  'growth.TRIAL_EMAILS': boolean;
  'growth.B2B_REFERRALS': boolean;

  // Creator
  'creator.CONTENT_SUBMISSION': boolean;
  'creator.PEER_REVIEW': boolean;
  'creator.PAYMENTS': boolean;
  'creator.REVENUE_SHARE': boolean;
  'creator.CME_CREDITS': boolean;

  // Mentorship
  'mentorship.VOLUNTEER': boolean;
  'mentorship.MATCHING': boolean;
  'mentorship.EXPERT_SESSIONS': boolean;
  'mentorship.PAYMENTS': boolean;

  // B2B
  'b2b.ORG_ACCOUNTS': boolean;
  'b2b.ORG_DASHBOARD': boolean;
  'b2b.ORG_ANALYTICS': boolean;
  'b2b.ORG_CHALLENGES': boolean;
  'b2b.ORG_SSO': boolean;

  // Voice
  'core.VOICE_STUDY_MODE': boolean;

  // Interview Prep
  'core.INTERVIEW_PREP': boolean;
  'creator.INTERVIEW_QUESTIONS': boolean;

  // Admin
  'admin.CONTENT_CMS': boolean;
  'admin.USER_MANAGEMENT': boolean;
  'admin.ANALYTICS': boolean;
  'admin.FEATURE_FLAGS': boolean;
}

// ---- Notification Types ----
export type NotificationType =
  | 'achievement_earned'
  | 'streak_reminder'
  | 'level_up'
  | 'question_approved'
  | 'question_rejected'
  | 'mentor_session_reminder'
  | 'new_comment'
  | 'challenge_started'
  | 'challenge_ended'
  | 'payout_completed';

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  message: string;
  data?: Record<string, unknown>;
  is_read: boolean;
  created_at: string;
}

// ---- Contact & CMS Types ----
export interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: 'new' | 'read' | 'replied' | 'archived';
  admin_note?: string;
  created_at: string;
  updated_at: string;
}

export type CMSContentType = 'blog_post' | 'job_posting' | 'announcement';
export type CMSContentStatus = 'draft' | 'published' | 'archived';

export interface CMSContent {
  id: string;
  content_type: CMSContentType;
  slug: string;
  title: string;
  excerpt?: string;
  body?: string;
  category?: string;
  metadata: Record<string, unknown>;
  status: CMSContentStatus;
  published_at?: string;
  created_at: string;
  updated_at: string;
}

// ---- Webhook & Rate Limiting Types (backend) ----
export interface WebhookEvent {
  event_id: string;
  event_type: string;
  processed_at: string;
  result?: Record<string, unknown>;
}

// ---- CME Credit Types ----
// Note: CME Credits feature is currently disabled (feature flag OFF)
// This infrastructure is prepared for Phase 2 when accreditation is obtained

export type CMECategory = 'category_1' | 'category_2' | 'self_assessment';
export type CMEActivityType = 'question_practice' | 'module_completion' | 'assessment' | 'mentorship';
export type CMEStatus = 'pending' | 'approved' | 'expired';

export interface CMECredit {
  id: string;
  user_id: string;
  activity_type: CMEActivityType;
  activity_id: string; // Reference to the activity (question batch, module, etc.)
  category: CMECategory;
  credits_earned: number;
  status: CMEStatus;
  approved_at?: string;
  expires_at?: string;
  certificate_url?: string;
  created_at: string;
}

export interface CMEProgress {
  user_id: string;
  certification_cycle_start: string;
  certification_cycle_end: string;

  // Credits by category
  category_1_earned: number;
  category_1_required: number;
  category_2_earned: number;
  category_2_required: number;
  self_assessment_earned: number;
  self_assessment_required: number;

  // Totals
  total_earned: number;
  total_required: number;

  // Deadline tracking
  days_until_deadline: number;
  is_on_track: boolean;

  updated_at: string;
}

export interface CMEActivity {
  id: string;
  title: string;
  description: string;
  activity_type: CMEActivityType;
  category: CMECategory;
  credits_available: number;
  estimated_duration_minutes: number;
  requirements: string[];
  is_completed: boolean;
  completed_at?: string;
}

export interface CMECertificate {
  id: string;
  user_id: string;
  certificate_number: string;
  credits_total: number;
  credits_breakdown: {
    category_1: number;
    category_2: number;
    self_assessment: number;
  };
  issue_date: string;
  expiry_date: string;
  pdf_url: string;
  verification_url: string;
}

// ---- Voice Study Mode Types ----
export type VoicePhase =
  | 'idle'
  | 'reading_question'
  | 'reading_options'
  | 'listening'
  | 'processing'
  | 'reading_feedback'
  | 'waiting_next';

export type VoiceCommand = 'a' | 'b' | 'c' | 'd' | 'skip' | 'next' | 'repeat' | 'stop' | 'bookmark' | 'explain';
